import * as React from 'react'
import upload from '@assets/Ico_Upload.png'
import check from '@assets/Ico_check_conferma.png'
import icoChiudi from '@assets/Ico_chiudi_verde.png'
import photo from '@assets/Ico_take_photo.png'

interface UploadBoxProps {
	title: string
	file: File | null
	onFile: (file?: File) => void
	onRemove?: () => void
	mode?: 'upload' | 'camera'
}

const MAX_FILE_SIZE_MB = 5
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'tiff', 'heic', 'heif', 'pdf']

function getFileExtension(file: File) {
	return file.name.split('.').pop()?.toLowerCase() ?? ''
}

function validateFile(
	file: File
): { title: string; description: string } | null {
	const ext = getFileExtension(file)

	if (!ALLOWED_EXTENSIONS.includes(ext)) {
		return {
			title: 'Il formato del file non è supportato',
			description:
				'Puoi caricare file in formato JPG, PNG, TIFF, HEIC/HEIF o PDF di dimensione non superiori a 5MB.',
		}
	}

	const isOverweight = file.size / 1024 / 1024 > MAX_FILE_SIZE_MB
	if (isOverweight) {
		return {
		title: 'La dimensione del file è troppo grande',
			description:
				'Puoi caricare file non superiori a 5MB, in formato JPG, PNG, TIFF, HEIC/HEIF o PDF.',
		}
	}

	return null
}

export function UploadBox({
	title,
	file,
	onFile,
	onRemove,
	mode = 'upload',
}: UploadBoxProps) {
	const fileInputRef = React.useRef<HTMLInputElement | null>(null)
	const cameraInputRef = React.useRef<HTMLInputElement | null>(null)

	const [error, setError] = React.useState<{
		title: string
		description: string
	} | null>(null)

	const handleValidatedFile = (f?: File) => {
		if (!f) return

		const validationError = validateFile(f)

		if (validationError) {
			setError(validationError)
			// oppure salva tutto l'oggetto se vuoi
			// setError(validationError)
			onFile(undefined)
			return
		}

		setError(null)
		onFile(f)
	}

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		if (mode === 'camera') return

		const f = e.dataTransfer.files?.[0]
		handleValidatedFile(f)
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0]
		handleValidatedFile(f)
		e.target.value = ''
	}

	const openPicker = () => {
		if (file) return
		if (mode === 'camera') cameraInputRef.current?.click()
		else fileInputRef.current?.click()
	}

	return (
		<div>
			<p className='text-center font-bold mb-2'>{title}</p>

			<input
				ref={fileInputRef}
				type='file'
				className='hidden'
				onChange={handleChange}
				accept='.jpg,.jpeg,.png,.tiff,.heic,.heif,.pdf,image/jpeg,image/png,image/tiff,application/pdf'
			/>

			<input
				ref={cameraInputRef}
				type='file'
				className='hidden'
				accept='image/*'
				capture='environment'
				onChange={handleChange}
			/>

			<div
				onDrop={handleDrop}
				onDragOver={(e) => {
					if (mode === 'upload') e.preventDefault()
				}}
				className='block'
			>
				<div
					onClick={openPicker}
					role='button'
					tabIndex={0}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') openPicker()
					}}
					className={`
            border border-dashed rounded-[17px]
            text-center transition relative
           w-full mx-auto min-h-[114px] md:min-h-[185px]
flex flex-col items-center justify-center
p-3
            cursor-pointer
            ${
							file
								? 'border-(--green-default) bg-white'
								: 'border-(--green-default) hover:bg-(--backgound-green-ligth) hover:border-solid hover:border-2 hover:border-(--brand-green)'
						}
          `}
				>
					{!file && (
						<>
							<div className='flex flex-col items-center justify-center mb-2 bg-(--background-Apple-green) rounded-[15px] w-[274px] min-h-[58px] py-2 mx-auto'>
								<div className='flex items-center justify-center w-full'>
									<img
										src={mode === 'camera' ? photo : upload}
										alt=''
										className='w-5 h-5'
									/>
								</div>
								<p className='font-semibold text-(--brand-green) text-[18px] whitespace-nowrap'>
									{mode === 'camera'
										? 'Fotografa il documento'
										: 'Trascina il file'}
								</p>
							</div>

							<p className='font-semibold'>
								Oppure{' '}
								<button
									type='button'
									className='text-(--green-default) underline font-semibold'
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
										fileInputRef.current?.click()
									}}
								>
									importa dai tuoi file
								</button>
							</p>
						</>
					)}

					{file && (
						<div className='flex flex-col items-center justify-center gap-3 w-full max-w-full min-w-0'>
							<div className='items-center justify-center text-white text-2xl'>
								<img src={check} alt='' className='w-12 h-12' />
							</div>

							<div className='flex flex-row gap-3 items-center w-full max-w-full min-w-0 px-4'>
								<p className='font-semibold truncate min-w-0 flex-1 text-center'>
									{file.name}
								</p>

								<button
									type='button'
									className='shrink-0'
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
										setError(null)
										onRemove?.()
										if (fileInputRef.current) fileInputRef.current.value = ''
										if (cameraInputRef.current)
											cameraInputRef.current.value = ''
									}}
								>
									<img src={icoChiudi} alt='' className='w-2.5 h-2.5' />
								</button>
							</div>
						</div>
					)}

					{/*   {file && (
            <div className="flex flex-col items-center justify-center p-10 gap-3">
              <div className="items-center justify-center text-white text-2xl">
                <img src={check} alt="" className="w-12 h-12" />
              </div>

              <div className="flex flex-row gap-3 items-center">
                <p className="font-semibold truncate max-w-[90%]">{file.name}</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setError(null)
                    onRemove?.()
                    if (fileInputRef.current) fileInputRef.current.value = ''
                    if (cameraInputRef.current) cameraInputRef.current.value = ''
                  }}
                >
                  <img src={icoChiudi} alt="" className="w-2.5 h-2.5" />
                </button>
              </div>
            </div>
          )} */}
				</div>
			</div>

			{error && (
				<div className='mt-3 p-3 text-[13px] text-(--text-red-alert)'>
					<div className='font-bold'>{error.title}</div>
					<div>{error.description}</div>
				</div>
			)}
		</div>
	)
}
